<script setup lang="ts">
    import { computed, onBeforeMount, onBeforeUnmount, ref, toRefs } from "vue";
    import emitter from "tiny-emitter/instance";
    import { AxiosError } from "axios";
    import store from "@/store";
    import { flashWarning } from "@/utils";
    import { RollType } from "@/types";
    import { defaultAutocompleteConverter } from "@/components/forms/inputs/Autocomplete/helpers";
    import { defaultRepeaterConverter } from "@/components/forms/groups/Repeater/helpers";
    import AdminLockBorder from "@/components/AdminLockBorder.vue";
    import KForm from "@/components/forms/Form/KForm.vue";
    import { useKForm } from "@/composables/useKForm";
    import { NumberValidationRule } from "@/components/forms/inputs/Number/numberValidation";
    import { Besoksmall } from "@/tabs/Oppenvard/types";
    import { Trigger } from "@/components/forms/Form/types";

    const props = defineProps<{
        tabId: number;
        parameters?: {
            besoksmallId: number;
        };
    }>();

    const { parameters, tabId } = toRefs(props);

    const { inputErrors, formErrors, handleErrorResponse } = useKForm();

    const formRef = ref();

    const besoksmall = computed(() =>
        store.getters["besoksmall/besoksmallById"]({
            id: parameters?.value?.besoksmallId,
        })
    );

    const isEditing = computed(() => !!besoksmall.value);

    const hasUnsavedData = computed(() => formRef.value?.hasUnsavedData());

    defineExpose({
        hasUnsavedData,
        tabId,
    });

    const formTriggers: Trigger[] = [
        {
            fieldName: "namn",
            dependentOn: "typ",
            action: ({
                value,
                dependentOnValue,
                initial = false,
            }: {
                value: unknown;
                dependentOnValue: unknown;
                initial?: boolean | undefined;
            }) => {
                if (!initial) {
                    // Föreslår namn baserat på typen
                    const typ = store.getters["besokstyp/besokstypById"]({ id: dependentOnValue });
                    value = typ?.namn ?? null;
                }
                return { value };
            },
        },
        {
            fieldName: "antal_patienter",
            dependentOn: "typ",
            action: ({ value, dependentOnValue }: { value: unknown; dependentOnValue: unknown }) => {
                const typ = store.getters["besokstyp/besokstypById"]({ id: dependentOnValue });
                if (!typ?.gruppbesok) {
                    return { value: null, disabled: true };
                }
                return { value, disabled: false };
            },
        },
    ];

    const setTabName = () => {
        store.commit("tabs/setTabName", {
            id: tabId.value,
            name: isEditing.value ? "Redigera besöksmall" : "Lägg till besöksmall",
        });
    };

    // TODO convert to useTabs in v3
    const closeTab = () => {
        if (formRef.value.hasUnsavedData()) {
            emitter.emit("open-dialog", {
                type: "warning",
                description: "Du har osparad data. Vill du stänga utan att spara?",
                buttons: [
                    {
                        title: "Fortsätt redigera",
                        type: "secondary",
                    },
                    {
                        title: "Ja, stäng utan att spara",
                        type: "primary",
                        action: () => {
                            store.dispatch("tabs/closeTab", tabId.value);
                        },
                    },
                ],
            });

            return;
        }

        store.dispatch("tabs/closeTab", tabId.value);
    };

    const onSubmit = async (besoksmall: Besoksmall) => {
        try {
            if (isEditing.value) {
                await store.dispatch("besoksmall/updateBesoksmall", {
                    besoksmall,
                });
            } else {
                await store.dispatch("besoksmall/createBesoksmall", {
                    besoksmall,
                });
            }

            // TODO: Det borde inte behöva göras en ny hämtning manuellt, men av någon anledning fylls inte
            // nuvarande filter på med nya objekt. (Funkar nedanstående även utan page och limit?)
            await store.dispatch("besoksmall/fetchBesoksmallar", {});

            closeTab();
        } catch (thrownError) {
            const error = thrownError as AxiosError;

            handleErrorResponse(error);
        }
    };

    const fetchBesokstyper = async ({ query }: { query?: string } = {}) => {
        try {
            const params: { q?: string; [key: string]: unknown } = {};

            if (query) {
                params.q = query;
            }

            params.aktiv = true;

            const { subsetName } = await store.dispatch("besokstyp/fetchBesokstyper", {
                filterParams: params,
                displayMode: true,
                limit: 0,
            });

            return store.getters["besokstyp/besokstypSubset"]({ subsetName, displayMode: true });
        } catch (error) {
            flashWarning("Kunde inte hämta besökstyper");
        }
    };

    const fetchFormData = async () => {
        if (besoksmall.value) {
            return {
                ...besoksmall.value,
                roller: besoksmall.value.roller_display.map((roll: RollType) => {
                    return {
                        id: {
                            value: roll.id,
                            label: roll.displayName,
                        },
                    };
                }),
            };
        }

        return {
            ordning: 0,
            aktiv: true,
        };
    };

    onBeforeMount(async () => {
        await store.dispatch("besoksmall/addSubscriber");

        setTabName();
    });

    onBeforeUnmount(async () => {
        await store.dispatch("besoksmall/removeSubscriber");
    });
</script>

<template>
    <div class="besoksmall-form">
        <KForm
            ref="formRef"
            id="besoksmall"
            :initialValuesGetter="fetchFormData"
            :submit="onSubmit"
            submitContent="Spara och stäng"
            cancelContent="Avbryt"
            :errors="inputErrors"
            :formErrors="formErrors"
            :triggers="formTriggers"
            @cancel="closeTab"
        >
            <template #header>
                <AdminLockBorder />
                <h2 v-if="isEditing">Redigera besöksmall</h2>
                <h2 v-else>Lägg till besöksmall</h2>
            </template>
            <template #default>
                <div class="form-content">
                    <KAutocomplete
                        name="typ"
                        label="Besökstyp"
                        :options="fetchBesokstyper"
                        :onSearch="fetchBesokstyper"
                        validation="required"
                    />
                    <KText name="namn" label="Namn" validation="required" />
                    <KRepeater
                        name="roller"
                        addLabel="Lägg till roll"
                        removeLabel="Ta bort roll"
                        :converter="defaultRepeaterConverter"
                        #default="{ index, model }"
                    >
                        <KAutocomplete
                            name="id"
                            url="/core/kodverk/roller/"
                            label="Roll"
                            :value="model?.[index]?.id ?? null"
                            class="span-all"
                            :suffixText="model?.[index]?.suffix"
                            :converter="defaultAutocompleteConverter"
                        />
                    </KRepeater>
                    <KNumber
                        name="standardtid"
                        label="Standardtid (min)"
                        :validation="[[NumberValidationRule.Min, 1]]"
                    />
                    <KNumber
                        name="antal_patienter"
                        label="Antal patienter"
                        :validation="[[NumberValidationRule.Min, 0]]"
                    />
                    <KNumber name="ordning" label="Ordning" :validation="[[NumberValidationRule.Min, 0]]" />
                    <KCheckbox name="aktiv" label="Aktiv" valueType="boolean" valign="center" />
                </div>
            </template>
        </KForm>
    </div>
</template>

<style lang="scss" scoped></style>
